/**
 * Upload service for handling file uploads to S3 via presigned URLs
 * Used for logo uploads and creative uploads
 */

import { api } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface GetUploadUrlOptions {
  projectId: string;
  fileName: string;
  contentType: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface UploadProgressCallback {
  (progress: number, loaded: number, total: number): void;
}

export interface UploadOptions {
  onProgress?: UploadProgressCallback;
  signal?: AbortSignal;
}

// ============================================================================
// UPLOAD FUNCTIONS
// ============================================================================

/**
 * Upload a file to S3 using a presigned URL
 * @param file - The file to upload
 * @param presignedUrl - The presigned S3 URL
 * @param options - Upload options (progress callback, abort signal)
 */
export async function uploadFile(
  file: File,
  presignedUrl: string,
  options?: UploadOptions
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Handle abort signal
    if (options?.signal) {
      options.signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    // Track upload progress
    if (options?.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          options.onProgress!(progress, event.loaded, event.total);
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`)
        );
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Perform upload
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * Upload a blob to S3 using a presigned URL
 * @param blob - The blob to upload
 * @param presignedUrl - The presigned S3 URL
 * @param contentType - The content type of the blob
 */
export async function uploadBlob(
  blob: Blob,
  presignedUrl: string,
  contentType: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`)
        );
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(blob);
  });
}

/**
 * Get a presigned URL for uploading a logo
 * @param projectId - The project ID
 * @param file - The logo file
 * @returns Presigned URL and S3 key
 */
export async function getLogoUploadUrl(
  projectId: string,
  file: File
): Promise<UploadUrlResponse> {
  const response = await api.post<UploadUrlResponse>('/upload/logo', {
    projectId,
    fileName: file.name,
    contentType: file.type,
  });

  return response;
}

/**
 * Upload a logo file to S3
 * @param projectId - The project ID
 * @param file - The logo file
 * @param options - Upload options (progress callback, abort signal)
 * @returns The S3 key of the uploaded file
 */
export async function uploadLogo(
  projectId: string,
  file: File,
  options?: UploadOptions
): Promise<string> {
  // Get presigned URL
  const { uploadUrl, s3Key } = await getLogoUploadUrl(projectId, file);

  // Upload file to S3
  await uploadFile(file, uploadUrl, options);

  // Return the S3 key (which can be used to construct the logo URL)
  return s3Key;
}

/**
 * Get a presigned URL for uploading a creative
 * @param options - Upload options
 * @returns Presigned URL response
 */
export async function getUploadUrl(
  options: GetUploadUrlOptions
): Promise<UploadUrlResponse> {
  const { projectId, fileName, contentType } = options;

  const response = await api.post<UploadUrlResponse>('/creatives/upload-url', {
    projectId,
    fileName,
    contentType,
  });

  return response;
}

/**
 * Upload a creative file to S3
 * @param projectId - The project ID
 * @param file - The creative file
 * @param options - Upload options (progress callback, abort signal)
 * @returns Upload URL response with S3 key
 */
export async function uploadCreative(
  projectId: string,
  file: File,
  options?: UploadOptions
): Promise<UploadUrlResponse> {
  // Get presigned URL
  const urlResponse = await getUploadUrl({
    projectId,
    fileName: file.name,
    contentType: file.type,
  });

  // Upload file to S3
  await uploadFile(file, urlResponse.uploadUrl, options);

  return urlResponse;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Validate file size
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB
 * @returns True if valid, false otherwise
 */
export function validateFileSize(file: File, maxSizeMB: number): boolean {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
}

/**
 * Validate file type
 * @param file - File to validate
 * @param allowedTypes - Array of allowed MIME types or extensions
 * @returns True if valid, false otherwise
 */
export function validateFileType(
  file: File,
  allowedTypes: string[]
): boolean {
  return allowedTypes.some((type) => {
    if (type.startsWith('.')) {
      return file.name.toLowerCase().endsWith(type.toLowerCase());
    }
    return file.type === type || file.type.startsWith(type);
  });
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
